import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, Bot, User, Loader, Zap, Code, Play, Download } from 'lucide-react';
import { useProjectStore } from '../../stores/projectStore';
import { useAuthStore } from '../../stores/authStore';
import { StanAPIService } from '../../services/StanAPIService';
import { WebSocketService } from '../../services/WebSocketService';

interface ChatMessage {
  id: string;
  type: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: Date;
  metadata?: {
    tokenCount?: number;
    agentType?: string;
    buildUrl?: string;
    files?: Array<{ name: string; content: string }>;
  };
}

interface AIChatPanelProps {
  className?: string;
}

export function AIChatPanel({ className }: AIChatPanelProps) {
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: '1',
      type: 'system',
      content: "👋 Hey! I'm Stan, your AI game development assistant. I can help you:\n\n• Edit your game scripts and logic\n• Build WebGL previews instantly\n• Generate new game features\n• Debug and optimize your code\n\nWhat would you like to work on?",
      timestamp: new Date(),
    }
  ]);
  
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  
  const { currentProject, currentFile, openFile, updateFile } = useProjectStore();
  const { token, user } = useAuthStore();

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Initialize WebSocket connection
  useEffect(() => {
    if (currentProject && token) {
      const userId = user?.id || localStorage.getItem('userId') || 'unknown';
      WebSocketService.connect(currentProject.id, userId, token);
      
      // Listen for WebSocket messages
      const unsubscribe = WebSocketService.onMessage((message) => {
        handleWebSocketMessage(message);
      });

      // Listen for connection status
      const unsubscribeStatus = WebSocketService.onConnectionChange((connected) => {
        setIsConnected(connected);
      });

      return () => {
        unsubscribe();
        unsubscribeStatus();
      };
    }
  }, [currentProject, token, user]);

  const handleWebSocketMessage = (wsMessage: any) => {
    if (wsMessage.type === 'agent_response') {
      const newMessage: ChatMessage = {
        id: Date.now().toString(),
        type: 'assistant',
        content: wsMessage.content || wsMessage.message,
        timestamp: new Date(),
        metadata: {
          agentType: wsMessage.agentType,
          tokenCount: wsMessage.tokenCount,
          buildUrl: wsMessage.buildUrl,
          files: wsMessage.files,
        }
      };
      
      setMessages(prev => [...prev, newMessage]);
      setIsLoading(false);
    } else if (wsMessage.type === 'task_started') {
      const taskMessage: ChatMessage = {
        id: Date.now().toString(),
        type: 'system',
        content: `⚡ Working on task: ${wsMessage.taskTitle || 'Unknown Task'}`,
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, taskMessage]);
    } else if (wsMessage.type === 'task_completed') {
      const doneMessage: ChatMessage = {
        id: Date.now().toString(),
        type: 'system',
        content: `✅ Task completed: ${wsMessage.taskTitle || 'Unknown Task'}`,
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, doneMessage]);
    } else if (wsMessage.type === 'build_complete') {
      const buildMessage: ChatMessage = {
        id: Date.now().toString(),
        type: 'system',
        content: `🎮 Build complete! Your game is ready to play.`,
        timestamp: new Date(),
        metadata: {
          buildUrl: wsMessage.buildUrl || wsMessage.play_url || wsMessage.playUrl,
        }
      };
      
      setMessages(prev => [...prev, buildMessage]);
    } else if (wsMessage.type === 'error') {
      const errorMessage: ChatMessage = {
        id: Date.now().toString(),
        type: 'system',
        content: `❌ Error: ${wsMessage.message}`,
        timestamp: new Date(),
      };
      
      setMessages(prev => [...prev, errorMessage]);
      setIsLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading || !currentProject) return;

    const userMessageContent = inputValue.trim();
    const userId = user?.id || localStorage.getItem('userId') || 'unknown';
    
    // Add file context if available
    let messageToSend = userMessageContent;
    if (currentFile) {
      const fileContext = `\n\n[Context: User is editing ${currentFile.path}]\n\`\`\`${currentFile.content.substring(0, 1000)}${currentFile.content.length > 1000 ? '...' : ''}\`\`\``;
      messageToSend += fileContext;
    }

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      type: 'user',
      content: userMessageContent, // Display original message to user
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      // Send message via WebSocket for real-time response
      if (isConnected) {
        await WebSocketService.sendMessage({
          type: 'user_message',
          content: messageToSend, // Send enriched message to AI
          message: messageToSend, // Fallback for some handlers
          projectId: currentProject.id,
          source: 'ide',
          userId: localStorage.getItem('firebase_uid') || 'unknown'
        });
      } else {
        // Fallback to HTTP API
        const response = await StanAPIService.sendChatMessage(
          currentProject.id,
          messageToSend,
          'ide'
        );

        const assistantMessage: ChatMessage = {
          id: Date.now().toString(),
          type: 'assistant',
          content: response.message,
          timestamp: new Date(),
          metadata: {
            tokenCount: response.tokenCount,
            buildUrl: response.buildUrl,
            files: response.files,
          }
        };

        setMessages(prev => [...prev, assistantMessage]);
        setIsLoading(false);
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      
      const errorMessage: ChatMessage = {
        id: Date.now().toString(),
        type: 'system',
        content: '❌ Failed to send message. Please try again.',
        timestamp: new Date(),
      };
      
      setMessages(prev => [...prev, errorMessage]);
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const renderMessage = (message: ChatMessage) => {
    const isUser = message.type === 'user';
    const isSystem = message.type === 'system';

    return (
      <motion.div
        key={message.id}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className={`message ${message.type}`}
      >
        <div className="message-header">
          <div className="message-avatar">
            {isUser ? (
              <User size={16} />
            ) : isSystem ? (
              <Zap size={16} />
            ) : (
              <Bot size={16} />
            )}
          </div>
          <div className="message-info">
            <span className="message-sender">
              {isUser ? 'You' : isSystem ? 'System' : 'Stan'}
            </span>
            <span className="message-time">
              {message.timestamp.toLocaleTimeString([], { 
                hour: '2-digit', 
                minute: '2-digit' 
              })}
            </span>
          </div>
          {message.metadata?.agentType && (
            <span className="agent-badge">
              {message.metadata.agentType}
            </span>
          )}
        </div>
        
        <div className="message-content">
          <pre className="message-text">{message.content}</pre>
          
          {/* Action buttons for special message types */}
          {message.metadata?.buildUrl && (
            <div className="message-actions">
              <button 
                className="action-button primary"
                onClick={() => window.open(message.metadata!.buildUrl, '_blank')}
              >
                <Play size={14} />
                Play Game
              </button>
            </div>
          )}
          
          {message.metadata?.files && message.metadata.files.length > 0 && (
            <div className="message-files">
              <div className="files-header">Generated Files:</div>
              {message.metadata.files.map((file, index) => (
                <div key={index} className="file-item">
                  <Code size={14} />
                  <span className="file-name">{file.name}</span>
                  <button 
                    className="file-action"
                    onClick={() => {
                      // Try to find the file in the project
                      const fileObj = currentProject?.files.find(f => f.path.endsWith(file.name) || f.path === file.name);
                      
                      if (fileObj) {
                        if (file.content) {
                          updateFile(fileObj.path, file.content);
                        }
                        openFile(fileObj.path);
                      } else {
                        console.warn('File not found in project:', file.name);
                      }
                    }}
                  >
                    {file.content ? 'Apply & Open' : 'Open'}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </motion.div>
    );
  };

  const quickActions = [
    {
      label: 'Add double jump',
      icon: <Zap size={14} />,
      action: () => setInputValue('Add a double jump mechanic to the player controller'),
    },
    {
      label: 'Create enemy AI',
      icon: <Bot size={14} />,
      action: () => setInputValue('Create a simple enemy AI that follows the player'),
    },
    {
      label: 'Build WebGL',
      icon: <Play size={14} />,
      action: () => setInputValue('Build a WebGL version of the game'),
    },
    {
      label: 'Add sound effects',
      icon: <Download size={14} />,
      action: () => setInputValue('Add sound effects for jumping and collecting items'),
    },
  ];

  return (
    <div className={`stan-panel ai-chat-panel ${className}`}>
      <div className="panel-header">
        <div className="panel-title">
          <Bot size={16} />
          Stan AI Assistant
        </div>
        <div className="panel-toolbar">
          <div className={`connection-status ${isConnected ? 'connected' : 'disconnected'}`}>
            <div className="status-dot" />
            {isConnected ? 'Connected' : 'Disconnected'}
          </div>
        </div>
      </div>

      <div className="panel-content chat-content">
        {/* Messages */}
        <div className="messages-container">
          <AnimatePresence>
            {messages.map(renderMessage)}
          </AnimatePresence>
          
          {isLoading && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="message assistant loading"
            >
              <div className="message-header">
                <div className="message-avatar">
                  <Loader size={16} className="spinning" />
                </div>
                <div className="message-info">
                  <span className="message-sender">Stan</span>
                  <span className="message-time">Thinking...</span>
                </div>
              </div>
              <div className="message-content">
                <div className="typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </motion.div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        {/* Quick Actions */}
        {messages.length <= 1 && (
          <div className="quick-actions">
            <div className="quick-actions-title">Quick Actions:</div>
            <div className="quick-actions-grid">
              {quickActions.map((action, index) => (
                <button
                  key={index}
                  className="quick-action-button"
                  onClick={action.action}
                >
                  {action.icon}
                  {action.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Input Area */}
        <div className="chat-input-container">
          <div className="chat-input-wrapper">
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={
                currentProject 
                  ? "Ask Stan to modify your game..." 
                  : "Load a project to start chatting with Stan"
              }
              disabled={!currentProject || isLoading}
              className="chat-input"
            />
            <button
              onClick={handleSendMessage}
              disabled={!inputValue.trim() || isLoading || !currentProject}
              className="send-button"
            >
              <Send size={16} />
            </button>
          </div>
          
          {currentProject && (
            <div className="input-hint">
              Press Enter to send • Shift+Enter for new line
            </div>
          )}
        </div>
      </div>

      <style jsx>{`
        .ai-chat-panel {
          display: flex;
          flex-direction: column;
          height: 100%;
        }

        .chat-content {
          display: flex;
          flex-direction: column;
          height: 100%;
          padding: 0;
        }

        .messages-container {
          flex: 1;
          overflow-y: auto;
          padding: 16px;
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .message {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .message-header {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .message-avatar {
          width: 24px;
          height: 24px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          background: var(--unity-bg-tertiary);
          color: var(--unity-text-secondary);
        }

        .message.user .message-avatar {
          background: var(--unity-accent-blue);
          color: white;
        }

        .message.assistant .message-avatar {
          background: var(--unity-accent-purple);
          color: white;
        }

        .message.system .message-avatar {
          background: var(--unity-accent-green);
          color: white;
        }

        .message-info {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .message-sender {
          font-size: 12px;
          font-weight: 600;
          color: var(--unity-text-primary);
        }

        .message-time {
          font-size: 10px;
          color: var(--unity-text-muted);
        }

        .agent-badge {
          background: var(--unity-bg-tertiary);
          color: var(--unity-text-secondary);
          padding: 2px 6px;
          border-radius: 10px;
          font-size: 10px;
          font-weight: 500;
        }

        .message-content {
          margin-left: 32px;
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .message-text {
          background: var(--unity-bg-tertiary);
          padding: 12px;
          border-radius: 8px;
          font-family: inherit;
          font-size: 13px;
          line-height: 1.4;
          white-space: pre-wrap;
          word-wrap: break-word;
          margin: 0;
        }

        .message.user .message-text {
          background: var(--unity-accent-blue);
          color: white;
        }

        .message-actions {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
        }

        .action-button {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 6px 12px;
          border: 1px solid var(--unity-border);
          background: var(--unity-bg-secondary);
          color: var(--unity-text-primary);
          border-radius: 4px;
          cursor: pointer;
          font-size: 12px;
          transition: all 0.15s ease;
        }

        .action-button:hover {
          background: var(--unity-bg-tertiary);
        }

        .action-button.primary {
          background: var(--unity-accent-green);
          border-color: var(--unity-accent-green);
          color: white;
        }

        .action-button.primary:hover {
          opacity: 0.9;
        }

        .message-files {
          background: var(--unity-bg-tertiary);
          border-radius: 6px;
          padding: 8px;
        }

        .files-header {
          font-size: 11px;
          font-weight: 600;
          color: var(--unity-text-secondary);
          margin-bottom: 6px;
        }

        .file-item {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 4px 0;
          font-size: 12px;
        }

        .file-name {
          flex: 1;
          color: var(--unity-text-primary);
        }

        .file-action {
          background: none;
          border: 1px solid var(--unity-border);
          color: var(--unity-text-secondary);
          padding: 2px 8px;
          border-radius: 3px;
          cursor: pointer;
          font-size: 10px;
        }

        .file-action:hover {
          background: var(--unity-bg-secondary);
          color: var(--unity-text-primary);
        }

        .typing-indicator {
          display: flex;
          gap: 4px;
          padding: 12px;
        }

        .typing-indicator span {
          width: 6px;
          height: 6px;
          background: var(--unity-text-secondary);
          border-radius: 50%;
          animation: typing 1.4s infinite ease-in-out;
        }

        .typing-indicator span:nth-child(2) {
          animation-delay: 0.2s;
        }

        .typing-indicator span:nth-child(3) {
          animation-delay: 0.4s;
        }

        @keyframes typing {
          0%, 60%, 100% {
            transform: translateY(0);
            opacity: 0.4;
          }
          30% {
            transform: translateY(-10px);
            opacity: 1;
          }
        }

        .quick-actions {
          padding: 16px;
          border-top: 1px solid var(--unity-border);
        }

        .quick-actions-title {
          font-size: 12px;
          font-weight: 600;
          color: var(--unity-text-secondary);
          margin-bottom: 8px;
        }

        .quick-actions-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 6px;
        }

        .quick-action-button {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 8px 10px;
          background: var(--unity-bg-tertiary);
          border: 1px solid var(--unity-border);
          color: var(--unity-text-primary);
          border-radius: 4px;
          cursor: pointer;
          font-size: 11px;
          transition: all 0.15s ease;
        }

        .quick-action-button:hover {
          background: var(--unity-accent-blue);
          border-color: var(--unity-accent-blue);
          color: white;
        }

        .chat-input-container {
          border-top: 1px solid var(--unity-border);
          padding: 12px;
        }

        .chat-input-wrapper {
          display: flex;
          gap: 8px;
          align-items: center;
        }

        .chat-input {
          flex: 1;
          background: var(--unity-bg-tertiary);
          border: 1px solid var(--unity-border);
          color: var(--unity-text-primary);
          padding: 8px 12px;
          border-radius: 6px;
          font-size: 13px;
          outline: none;
          transition: border-color 0.15s ease;
        }

        .chat-input:focus {
          border-color: var(--unity-accent-blue);
        }

        .chat-input:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .send-button {
          width: 36px;
          height: 36px;
          background: var(--unity-accent-blue);
          border: none;
          color: white;
          border-radius: 6px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.15s ease;
        }

        .send-button:hover:not(:disabled) {
          background: var(--unity-accent-purple);
        }

        .send-button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .input-hint {
          font-size: 10px;
          color: var(--unity-text-muted);
          margin-top: 4px;
          text-align: center;
        }

        .connection-status {
          display: flex;
          align-items: center;
          gap: 4px;
          font-size: 10px;
          color: var(--unity-text-muted);
        }

        .status-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: var(--unity-accent-red);
        }

        .connection-status.connected .status-dot {
          background: var(--unity-accent-green);
        }

        .spinning {
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}